import { ChangeEvent, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { LogoSvg, WarningIcon } from "@libs/zapi-ui/assets";
import { Input } from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";

const CredentialsSchema = z.object({
  email: z.string().email({ message: "Invalid email address" })
});

type TCredentials = z.infer<typeof CredentialsSchema>;

export function ForgotPasswordPage() {
  const [credentials, setCredentials] = useState<TCredentials>({ email: "" });
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const isValidCredentials = CredentialsSchema.safeParse(credentials).success;
  const mutation = useMutation({
    mutationFn: async (credentials: TCredentials) => {
      const payload = { email: credentials.email };
      return await $api.post("/auth/passwords/forgot", payload);
    },
    onSuccess: () => {
      toast.info("Check your email for a link to reset your password");
      setCredentials({ email: "" });
    },
    onError: (err: AxiosError<{ message: string }>) => {
      const message = err?.response?.data?.message ?? "Failed to reset password";
      toast.error(message);
    }
  });

  async function resetPassword() {
    if (!isValidCredentials) return;
    try {
      await mutation.mutateAsync(credentials);
    } catch {
      return;
    }
  }

  function onChangeEmail(e: ChangeEvent<HTMLInputElement>) {
    setEmailError(undefined);
    setCredentials((prev) => ({ ...prev, email: e.target.value.trim() }));
  }

  return (
    <div className={"flex flex-col items-center h-[calc(100vh - 40px)]"}>
      <LogoSvg className={"my-[64px]"} />
      <div className={"max-w-[400px] w-full"}>
        <div>
          <div className={"flex mb-[32px] flex-col gap-[16px]"}>
            <FigmaTypography component="h5" variant="heading-2">
              Forgot Password?
            </FigmaTypography>
            <FigmaTypography variant="body-2">
              Enter your email address and we will send you instructions to reset your password.
            </FigmaTypography>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              resetPassword();
            }}
          >
            <div className={"flex flex-col  gap-[16px]"}>
              <div className={"flex flex-col gap-[4px]"}>
                <label className={"text-inLabel font-medium mb-[4px]"}>Email</label>
                <Input
                  value={credentials.email}
                  onChange={onChangeEmail}
                  onBlur={() => {
                    setEmailError(
                      CredentialsSchema.safeParse(credentials).error?.issues.find((issue) => issue.path[0] === "email")
                        ?.message
                    );
                  }}
                  className={emailError && "invalid"}
                  required
                />
                <p className={"text-errorRed text-[12px] leading-[18px] flex items-center gap-1 h-[18px]"}>
                  {emailError && <WarningIcon width={18} height={18} />}
                  {emailError}
                </p>
              </div>
            </div>
            <AppButton
              color="orange400"
              className={"mt-1 w-full min-h-[40px] rounded-[28px]"}
              disabled={mutation.isPending}
              type="submit"
            >
              Reset Password
            </AppButton>
          </form>
        </div>
      </div>
    </div>
  );
}
